<script setup lang="ts">
import type { ErrorDto, SearchAutocompleteInputProperties, SuggestionContext, SuggestionItem } from '@optionway/models';
import type { LabelledFieldMode } from '~/components/ui-custom/labelled-field/models';
import { AlertCircle, Check, MapPin, X } from 'lucide-vue-next';
import { breakpointsTailwind } from '@vueuse/core';

const { locale } = useI18n();
const prefix = 'components.search.autocompleteInput';
// isHydrating destructured deliberately: only its setup-time value matters (see the capture below).
const { $appConfig, isHydrating } = useNuxtApp();
const apiUrl = $appConfig?.api?.url;
const config = $appConfig.app?.components?.searchAutocompleteInput;

const properties = withDefaults(
  defineProps<
    SearchAutocompleteInputProperties &
      SuggestionContext & {
        compact?: boolean;
        inBar?: boolean;
        error?: string;
        autoFocus?: boolean;
        labelMode?: LabelledFieldMode;
        forceFocus?: number;
      }
  >(),
  { error: undefined, labelMode: undefined, forceFocus: undefined, excludeTrainStations: undefined },
);
const emit = defineEmits<{
  select: [suggestion: SuggestionItem];
  'update:autoFocus': [value: false];
}>();

const componentConfig = computed<SearchAutocompleteInputProperties>(() => {
  return {
    label: properties.label ?? config?.label,
    minInputChar: properties.minInputChar ?? config?.minInputChar ?? 3,
    excludeTrainStations: properties.excludeTrainStations ?? config?.excludeTrainStations,
  };
});

const inputId = useId();
const errorId = `${inputId}-error`;
const selectedSuggestion = defineModel<SuggestionItem>();
const search = ref<string>('');
const searchTrigger = ref<string>('');

const sheetOpen = defineModel<boolean>('sheetOpen', { default: false });

const openSheet = () => {
  search.value = '';
  if (enrichedSelectedSuggestion.value?.cityLabel) {
    searchTrigger.value = enrichedSelectedSuggestion.value.cityLabel;
  }
  sheetOpen.value = true;
};

// Read only from focus() and the forceFocus watcher, both driven by user events: by then the component is
// mounted and the breakpoint reads the real viewport, not the mobile-first SSR width of plugins/03.ssr-width.ts.
const breakpoints = useBreakpoints(breakpointsTailwind);
// smaller, not smallerOrEqual: the mobile trigger is md:hidden, which already hides it at 768px.
const isMobile = breakpoints.smaller('md');

const focus = () => {
  if (isMobile.value) {
    openSheet();
  } else {
    comboboxInputFocused.value = true;
  }
};

const locationLabels = useLocationLabels();

const enrichedSelectedSuggestion = computed(() => locationLabels.enrichSuggestion(selectedSuggestion.value));

const searchCriteriaStore = useSearchCriteriaStore();

const sheetInputReference = useTemplateRef<HTMLInputElement>('sheetInput');
const { focused: sheetInputFocused } = useFocus(sheetInputReference);

const comboboxInputReference = useTemplateRef<HTMLInputElement>('comboboxInput');
const { focused: comboboxInputFocused } = useFocus(comboboxInputReference);
const activeElement = useActiveElement();

const mobileTriggerReference = useTemplateRef('mobileTrigger');
const { focused: mobileTriggerFocused } = useFocus(mobileTriggerReference);

// The mobile trigger is focused, not its sheet opened: the sheet does not show the error.
watch(
  () => properties.forceFocus,
  (request) => {
    if (request === undefined) {
      return;
    }
    if (isMobile.value) {
      mobileTriggerFocused.value = true;
    } else {
      comboboxInputFocused.value = true;
    }
  },
);

watch(sheetOpen, async (open) => {
  if (open) {
    await nextTick();
    sheetInputFocused.value = true;
  } else {
    searchTrigger.value = '';
  }
});

const selectedLabel = computed(() => {
  if (!enrichedSelectedSuggestion.value?.code) return '';
  return `${enrichedSelectedSuggestion.value.cityLabel ?? ''} (${enrichedSelectedSuggestion.value.code})`;
});

watch(search, (value) => {
  const minInputChar = componentConfig.value.minInputChar ?? 0;
  if (value.length >= minInputChar) {
    searchTrigger.value = value;
  }
});

// Taken over from reka-ui: its own resetSearchTermOnSelect also fires on mount and would wipe the restored input.
watch(selectedSuggestion, () => {
  search.value = '';
});

const {
  data: suggestions,
  isLoading,
  refresh: refreshSuggestions,
} = useQuery<SuggestionItem[], ErrorDto>({
  key: () =>
    searchCriteriaStore.suggestionsKey(
      locale.value,
      searchTrigger.value,
      properties,
      componentConfig.value.excludeTrainStations,
    ),
  query: () =>
    $fetch<SuggestionItem[]>(
      `${apiUrl}/search/suggestions/${locale.value}?${searchCriteriaStore.toSuggestionsQueryString(searchTrigger.value, properties, componentConfig.value.excludeTrainStations)}`,
    ),
  enabled: () => !!apiUrl && searchTrigger.value.length > 0,
  placeholderData: (previousData) => previousData,
});

const enrichedSuggestions = computed(() => locationLabels.enrichSuggestions(suggestions.value));

// A click on an option focuses the list before selecting: no fallback then, but the blur stays (the pick shows unfocused).
const pickFirstSuggestionUnlessListFocused = (event: FocusEvent) => {
  const listFocused = event.relatedTarget instanceof Element && !!event.relatedTarget.closest('[role="listbox"]');
  if (!selectedSuggestion.value?.code && !listFocused) {
    selectedSuggestion.value = suggestions.value?.[0];
  }
};

const selectFromSheet = (suggestion: SuggestionItem) => {
  selectedSuggestion.value = suggestion;
  sheetOpen.value = false;
  emit('select', suggestion);
};

watch(
  () => properties.autoFocus,
  (value) => {
    if (!value) return;
    focus();
    emit('update:autoFocus', false);
  },
  { flush: 'sync' },
);

// The SSR-rendered input works before hydration: the browser holds the keystrokes in its live DOM value, readable
// while this setup runs. The state takes it now, so the hydration patch writes back what the field already holds
// (noDirectDomAccess derogation); data-allow-mismatch="attribute" on the input covers the value typed since the markup.
const prehydrationInput = isHydrating ? document.querySelector<HTMLInputElement>(`input#${inputId}`) : undefined;
const prehydrationValue = prehydrationInput?.value ?? '';

if (prehydrationValue) {
  search.value = prehydrationValue;
  // reka-ui does not listen to the field until mounted: mirror every keystroke so nothing typed during hydration is lost.
  const mirrorPrehydrationInput = () => {
    search.value = prehydrationInput?.value ?? search.value;
  };
  prehydrationInput?.addEventListener('input', mirrorPrehydrationInput);

  whenever(
    () => unrefElement(comboboxInputReference),
    async (input) => {
      prehydrationInput?.removeEventListener('input', mirrorPrehydrationInput);
      await nextTick(); // let the suggestions query start from the mirrored value

      // still the field the user was writing in: replay the keystroke path, so reka-ui reopens its list
      if (activeElement.value === input) {
        input.dispatchEvent(new Event('input'));
        return;
      }

      // the user typed then moved on: replay the blur path, which picks the first suggestion
      if (!searchTrigger.value) {
        return;
      }
      await refreshSuggestions();
      input.dispatchEvent(new Event('blur'));
    },
    { once: true },
  );
}

if (import.meta.test) {
  defineExpose({ selectedSuggestion, search, suggestions, selectFromSheet });
}
</script>

<template>
  <div class="flex w-full min-w-0 flex-col gap-1">
    <!-- The error sits outside the labelled field: in floating mode its children share one grid cell, and the
       message must show under both the mobile trigger and the desktop combobox -->
    <UiCustomLabelledField
      v-slot="{ labelId }"
      :label="properties.label"
      :for="inputId"
      :mode="properties.compact ? 'none' : properties.labelMode"
      :floated="!!selectedLabel || !!search"
    >
      <!-- Mobile trigger -->
      <div class="md:hidden">
        <UiButton
          :id="`${inputId}-trigger`"
          ref="mobileTrigger"
          :aria-labelledby="
            !selectedLabel && properties.labelMode !== 'floating'
              ? `${inputId}-trigger`
              : `${labelId} ${inputId}-trigger`
          "
          :aria-invalid="!!properties.error"
          :aria-describedby="properties.error ? errorId : undefined"
          :class="
            cn(
              'flex w-full cursor-pointer items-center justify-start gap-2 rounded-lg border border-gray-500 bg-surface shadow-sm hover:bg-gray-50',
              properties.compact ? 'h-9 px-3 py-1.5 text-sm [&_svg]:size-4' : 'h-11 px-3.5 py-2.5 [&_svg]:size-5',
              properties.error && 'ring-2 ring-inset ring-red-700',
            )
          "
          @click="openSheet()"
        >
          <MapPin class="shrink-0 text-gray-500" />
          <span
            :class="[
              properties.compact ? 'text-sm' : 'text-base',
              'font-normal',
              selectedLabel ? 'text-text-primary-900' : 'text-gray-500',
            ]"
          >
            {{ selectedLabel || (properties.labelMode === 'floating' ? '' : properties.label) }}
          </span>
        </UiButton>
      </div>

      <!-- Mobile sheet -->
      <UiSheet v-model:open="sheetOpen">
        <UiSheetContent
          :side="properties.sheetSide ?? 'bottom'"
          hide-close
          :aria-describedby="undefined"
          class="bottom-auto top-0 flex h-dvh w-full flex-col gap-0 rounded-none p-0"
        >
          <UiSheetTitle class="sr-only">{{ properties.label }}</UiSheetTitle>
          <div class="flex flex-1 flex-col">
            <div class="flex items-center gap-3 p-4">
              <UiInput
                ref="sheetInput"
                v-model="search"
                :aria-label="properties.label"
                class="flex-1 border-gray-500 font-normal placeholder:text-gray-500"
                :class="{ 'placeholder:text-foreground': selectedSuggestion && !sheetInputFocused }"
                :placeholder="selectedLabel || properties.label"
                @blur="!selectedSuggestion?.code && (selectedSuggestion = suggestions?.[0])"
              />
              <UiSheetClose
                :aria-label="$t('components.common.close')"
                class="rounded-sm opacity-70 transition-opacity hover:opacity-100"
              >
                <X class="h-4 w-4" />
              </UiSheetClose>
            </div>

            <div class="h-2 shadow-md" />

            <div class="flex-1 overflow-y-auto py-2">
              <UiButton
                v-for="suggestion in enrichedSuggestions"
                :key="`${suggestion.type}-${suggestion.code}`"
                class="flex w-full items-center justify-between bg-transparent py-6 shadow-none hover:bg-transparent"
                @pointerdown.prevent
                @click="selectFromSheet(suggestion)"
              >
                <SearchSuggestionItem
                  :suggestion="suggestion"
                  :query="search"
                />
                <Check
                  v-if="selectedSuggestion?.code === suggestion.code"
                  class="size-5"
                />
              </UiButton>

              <div
                v-if="searchTrigger && enrichedSuggestions?.length === 0"
                class="flex flex-col p-4 text-sm"
              >
                <strong>{{ $t(`${prefix}.noResult`) }}</strong>
                <p class="pt-2">{{ $t(`${prefix}.checkInput`) }}</p>
              </div>
            </div>
          </div>
        </UiSheetContent>
      </UiSheet>

      <!-- Desktop combobox -->
      <div class="hidden md:block">
        <UiField
          class="gap-1"
          :data-invalid="!!properties.error"
        >
          <UiCombobox
            ref="comboboxReference"
            v-model="selectedSuggestion"
            :ignore-filter="true"
            :reset-search-term-on-select="false"
          >
            <UiComboboxAnchor class="w-full">
              <div class="relative">
                <UiComboboxInput
                  :id="inputId"
                  ref="comboboxInput"
                  v-model="search"
                  data-allow-mismatch="attribute"
                  :class="
                    cn(
                      'cursor-pointer bg-background font-normal text-text-primary-900 shadow-sm placeholder:text-gray-500 focus-visible:ring-brand-primary-500',
                      properties.inBar
                        ? 'rounded-none border-0 shadow-none hover:bg-gray-50 hover:shadow-none focus-visible:bg-gray-50 focus-visible:ring-0'
                        : 'rounded-lg border-gray-500 bg-surface',
                      properties.compact ? 'h-9 py-1.5 pl-9 pr-3 text-sm' : 'h-11 py-2.5 pl-10 pr-3.5 text-base',
                      selectedSuggestion && !comboboxInputFocused && 'placeholder:text-foreground',
                      properties.error && !properties.inBar && 'ring-2 ring-inset ring-red-700',
                    )
                  "
                  :placeholder="selectedLabel || (properties.compact ? properties.label : '')"
                  :aria-invalid="!!properties.error"
                  :aria-describedby="properties.error ? errorId : undefined"
                  autocomplete="off"
                  @blur="pickFirstSuggestionUnlessListFocused"
                />
                <span
                  :class="[
                    'pointer-events-none absolute inset-y-0 start-0 flex items-center',
                    properties.compact ? 'px-2.5' : 'px-3',
                  ]"
                >
                  <MapPin :class="[properties.compact ? 'size-4' : 'size-5', 'shrink-0 text-gray-500']" />
                </span>
                <span
                  v-if="properties.error"
                  :class="[
                    'pointer-events-none absolute inset-y-0 end-0 flex items-center',
                    properties.compact ? 'px-2.5' : 'px-3',
                  ]"
                >
                  <AlertCircle :class="[properties.compact ? 'size-4' : 'size-5', 'text-red-700']" />
                </span>
              </div>
            </UiComboboxAnchor>

            <UiComboboxList
              v-if="searchTrigger"
              :align="'start'"
              :side-offset="8"
              class="w-96"
            >
              <UiComboboxEmpty
                v-if="!isLoading"
                class="py-4 text-left"
              >
                <div class="ml-2 flex flex-col text-sm">
                  <div class="pl-2">
                    <strong>{{ $t(`${prefix}.noResult`) }}</strong>
                  </div>
                  <div class="pl-2 pt-2">
                    {{ $t(`${prefix}.checkInput`) }}
                  </div>
                </div>
              </UiComboboxEmpty>

              <UiComboboxGroup>
                <UiComboboxItem
                  v-for="suggestion in enrichedSuggestions"
                  :key="`${suggestion.type}-${suggestion.code}`"
                  :value="suggestion"
                  :class="{ 'bg-light-blue-50': selectedSuggestion?.code === suggestion.code }"
                >
                  <SearchSuggestionItem
                    :suggestion="suggestion"
                    :query="search"
                  />
                </UiComboboxItem>
              </UiComboboxGroup>
            </UiComboboxList>
          </UiCombobox>
        </UiField>
      </div>
    </UiCustomLabelledField>

    <Transition
      enter-active-class="transition-all duration-200 ease-out motion-reduce:transition-none"
      enter-from-class="opacity-0 -translate-y-1"
      leave-active-class="transition-all duration-150 ease-in motion-reduce:transition-none"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <UiFieldError
        v-if="properties.error"
        :id="errorId"
        class="text-xs text-red-700"
      >
        {{ properties.error }}
      </UiFieldError>
    </Transition>
  </div>
</template>
